import { showToast } from '../ui';

export type ToastFn = typeof showToast;

export function promptArchiveUrls(
  promptFn: (message: string, defaultValue?: string) => string | null = window.prompt
): string[] {
  const input = promptFn('Paste image URLs to archive (one per line or comma-separated):', '');
  if (input == null) {
    return [];
  }

  const text = String(input || '').trim();
  if (!text) {
    return [];
  }

  const normalized = text.split('\r\n').join('\n');
  const set = new Set<string>();

  normalized.split('\n').forEach(line => {
    if (!line) {
      return;
    }
    line.split(',').forEach(piece => {
      const raw = (piece || '').trim();
      if (raw) {
        set.add(raw);
      }
    });
  });

  return Array.from(set);
}

export function notifyArchiveSuccess(count: number, toast: ToastFn = showToast): void {
  if (count > 0) {
    toast(`Archived ${count} URL${count === 1 ? '' : 's'}.`, { type: 'success' });
  }
}

export function notifyArchiveFailure(error: unknown, toast: ToastFn = showToast): void {
  const message = error instanceof Error ? error.message : String(error);
  toast(`Failed to archive URLs: ${message}`, { type: 'error' });
}

export function notifyRemovalSuccess(
  count: number,
  successMessage: (count: number) => string,
  toast: ToastFn = showToast
): void {
  if (count > 0) {
    toast(successMessage(count), { type: 'success' });
  }
}

export function notifyRemovalFailures(
  remoteErrors: { error: Error }[],
  failureLabelSingular: string,
  failureLabelPlural: string,
  toast: ToastFn = showToast
): void {
  if (!remoteErrors.length) {
    return;
  }
  const firstError = remoteErrors[0]?.error;
  const message = firstError?.message ? firstError.message : String(firstError || 'Unknown error');
  const failedCount = remoteErrors.length;
  const failureLabel = failedCount === 1 ? failureLabelSingular : failureLabelPlural;
  toast(`Failed to remove ${failedCount} ${failureLabel}: ${message}`, { type: 'error' });
}

export function notifyRemovalError(
  prefix: string,
  error: unknown,
  toast: ToastFn = showToast
): void {
  const message = error instanceof Error ? error.message : String(error);
  toast(`${prefix}: ${message}`, { type: 'error' });
}

export function notifyDeleteSuccess(titleForToast: string, toast: ToastFn = showToast): void {
  toast(`${titleForToast} deleted from your list.`, { type: 'success' });
}

export function notifyDeleteFailure(
  titleForToast: string,
  error: unknown,
  toast: ToastFn = showToast
): void {
  const message = error instanceof Error ? error.message : String(error);
  toast(`Failed to delete ${titleForToast}: ${message}`, { type: 'error' });
}
